import React, { ReactElement, useState } from 'react'
import {
  Box,
  Button,
  Card,
  Chip,
  Collapse,
  Divider,
  IconButton,
  Typography,
} from '@mui/material'
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown'
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp'
import dayjs from 'dayjs'
import { AddIcon, DeleteIcon } from 'src/v3/core/icons'
import { useAppDispatch, useAppSelector } from 'src/store/hooks'
import {
  deletePendingCAPCE,
  deletePendingCAPCECourse,
  deleteCAPCE,
  deleteCAPCECourse,
  finalizePendingCAPCE,
  finalizePendingCAPCECourse,
  clearCapceOperationStatus,
  selectCapceOperationStatus,
  selectCapceOperationError,
  selectCapceOperationSuccess,
} from 'src/v3/content-pipeline/accreditation.slice'
import ConfirmAction from 'src/v3/components/confirm-action'
import { CAPCEPendingAccreditation, CAPCEFinalizedAccreditation } from './types'
import { CreateForm } from './create-form'
import { EditForm } from './edit-form'
import { FieldComparison, StatusActions, AutoTrackedDates } from '../shared'
import {
  getStatusColor,
  getAccreditationFlags,
  RejectedAlert,
  ApprovedAlert,
  OperationSnackbars,
  calculateFieldChanges,
} from '../../shared-utils'

type CAPCESectionProps = {
  eventId?: string
  courseId?: string
  finalized?: CAPCEFinalizedAccreditation
  pending?: CAPCEPendingAccreditation
}

export let CAPCESection = ({
  eventId,
  courseId,
  finalized,
  pending,
}: CAPCESectionProps): ReactElement => {
  let dispatch = useAppDispatch()
  let [formOpen, setFormOpen] = useState(false)
  let [editFormOpen, setEditFormOpen] = useState(false)
  let [expanded, setExpanded] = useState(false)
  let [confirm, setConfirm] = useState('')

  let operationStatus = useAppSelector(selectCapceOperationStatus)
  let operationError = useAppSelector(selectCapceOperationError)
  let operationSuccess = useAppSelector(selectCapceOperationSuccess)

  let submitting = operationStatus === 'pending'
  let { hasNoAccreditation, hasOnlyFinalized, hasOnlyPending, hasBoth } =
    getAccreditationFlags(finalized, pending)
  let statusColor = getStatusColor(pending?.status)

  let handleFinalize = () => {
    if (!pending?.id) return
    if (courseId) {
      dispatch(finalizePendingCAPCECourse({ id: pending.id, courseId }))
    } else if (eventId) {
      dispatch(finalizePendingCAPCE({ id: pending.id, eventId }))
    }
  }

  let changes = calculateFieldChanges(finalized, pending, [
    { key: 'courseCode', label: 'Course Code' },
    { key: 'topicArea', label: 'Topic Area' },
    { key: 'classType', label: 'Class Type' },
    { key: 'applicationDate', label: 'Application Date' },
    { key: 'submissionDate', label: 'Submission Date' },
    { key: 'allClearDate', label: 'All Clear Date' },
    { key: 'goLiveDate', label: 'Go Live Date' },
  ])

  return (
    <Box>
      <Card variant="outlined" sx={{ overflow: 'visible' }}>
        {/* Compact Header */}
        <Box className="p-4 grid grid-cols-[1fr_auto] gap-4 items-center">
          <Box className="flex items-center gap-4">
            <Typography variant="h6">CAPCE Accreditation</Typography>
            {finalized && (
              <>
                <Chip color="success" label="Finalized" size="small" />
                <Typography color="text.secondary" variant="body2">
                  {finalized.courseCode} • {finalized.topicArea} •{' '}
                  {finalized.classType}
                </Typography>
              </>
            )}
            {hasOnlyPending && pending && (
              <>
                <Chip
                  label={pending.status}
                  size="small"
                  sx={{
                    color: statusColor,
                  }}
                />
                <Typography color="text.secondary" variant="body2">
                  {pending.topicArea || 'Pending'} •{' '}
                  {pending.classType || 'Draft'}
                </Typography>
              </>
            )}
            {hasNoAccreditation && (
              <Chip color="default" label="Not Created" size="small" />
            )}
          </Box>

          <Box className="flex items-center gap-2">
            {hasBoth && pending && (
              <Chip
                color="warning"
                label={`${changes.length} ${changes.length === 1 ? 'Change' : 'Changes'}`}
                size="small"
              />
            )}
            {hasBoth && pending && (
              <Chip
                label={pending.status}
                size="small"
                sx={{
                  color: statusColor,
                }}
              />
            )}
            {(hasNoAccreditation || hasOnlyFinalized) && (
              <Button
                startIcon={<AddIcon />}
                variant="contained"
                size="small"
                onClick={() => setFormOpen(true)}
              >
                {hasNoAccreditation ? 'Create' : 'New Update'}
              </Button>
            )}
            {(finalized || pending) && (
              <IconButton
                size="small"
                onClick={() => setExpanded(!expanded)}
                aria-label={expanded ? 'Show less' : 'Show more'}
              >
                {expanded ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
              </IconButton>
            )}
          </Box>
        </Box>

        {pending?.status === 'Rejected' && (
          <RejectedAlert
            rejectedDate={pending.rejectedDate}
            notes={pending.notes}
          />
        )}

        {pending?.status === 'Approved' && (
          <ApprovedAlert approvedDate={pending.approvedDate} />
        )}

        {/* Expandable Details */}
        <Collapse in={expanded}>
          <Divider />
          <Box sx={{ p: 2 }}>
            {hasNoAccreditation && (
              <Typography color="text.secondary" variant="body2">
                No CAPCE accreditation has been created for this{' '}
                {courseId ? 'course' : 'event'}.
              </Typography>
            )}

            {/* Side-by-side comparison when both exist */}
            {hasBoth && finalized && pending && (
              <Box>
                <Box className="grid grid-cols-2 gap-4 mb-4">
                  <Typography
                    color="text.secondary"
                    variant="caption"
                    sx={{
                      fontWeight: 600,
                    }}
                  >
                    Current (Finalized)
                  </Typography>
                  <Typography
                    color="text.secondary"
                    variant="caption"
                    sx={{
                      fontWeight: 600,
                    }}
                  >
                    Pending Update
                  </Typography>
                </Box>

                <FieldComparison
                  label="Course Code"
                  finalizedValue={finalized.courseCode}
                  pendingValue={pending.courseCode}
                />
                <FieldComparison
                  label="Topic Area"
                  finalizedValue={finalized.topicArea}
                  pendingValue={pending.topicArea}
                />
                <FieldComparison
                  label="Class Type"
                  finalizedValue={finalized.classType}
                  pendingValue={pending.classType}
                />
                <FieldComparison
                  label="Application Date"
                  finalizedValue={finalized.applicationDate}
                  pendingValue={pending.applicationDate}
                  isDate
                />
                <FieldComparison
                  label="Submission Date"
                  finalizedValue={finalized.submissionDate}
                  pendingValue={pending.submissionDate}
                  isDate
                />
                <FieldComparison
                  label="All Clear Date"
                  finalizedValue={finalized.allClearDate ?? ''}
                  pendingValue={pending.allClearDate}
                  isDate
                />
                <FieldComparison
                  label="Go Live Date"
                  finalizedValue={finalized.goLiveDate ?? ''}
                  pendingValue={pending.goLiveDate}
                  isDate
                />

                <Box sx={{ mt: 2 }}>
                  <Typography
                    color="text.secondary"
                    variant="caption"
                    sx={{
                      fontWeight: 600,
                    }}
                  >
                    Expiration Date (Finalized)
                  </Typography>
                  <Typography variant="body2">
                    {dayjs(finalized.expirationDate).format('MMM D, YYYY')}
                  </Typography>
                </Box>

                {pending.notes && (
                  <Box sx={{ mt: 2, p: 2, bgcolor: 'background.paperDark' }}>
                    <Typography
                      color="text.secondary"
                      variant="caption"
                      sx={{
                        fontWeight: 600,
                      }}
                    >
                      Notes
                    </Typography>
                    <Typography variant="body2">{pending.notes}</Typography>
                  </Box>
                )}

                <StatusActions
                  status={pending.status}
                  submitting={submitting}
                  onEdit={() => setEditFormOpen(true)}
                  onDelete={() => setConfirm('delete-pending-capce')}
                  onFinalize={handleFinalize}
                />
              </Box>
            )}

            {/* Only finalized */}
            {hasOnlyFinalized && finalized && (
              <Box>
                <Box className="grid gap-3">
                  <Box>
                    <Typography color="text.secondary" variant="caption">
                      Course Code
                    </Typography>
                    <Typography variant="body2">
                      {finalized.courseCode}
                    </Typography>
                  </Box>
                  <Box className="grid grid-cols-2 gap-4">
                    <Box>
                      <Typography color="text.secondary" variant="caption">
                        Topic Area
                      </Typography>
                      <Typography variant="body2">
                        {finalized.topicArea}
                      </Typography>
                    </Box>
                    <Box>
                      <Typography color="text.secondary" variant="caption">
                        Class Type
                      </Typography>
                      <Typography variant="body2">
                        {finalized.classType}
                      </Typography>
                    </Box>
                  </Box>
                  <Box className="grid grid-cols-3 gap-4">
                    <Box>
                      <Typography color="text.secondary" variant="caption">
                        Application Date
                      </Typography>
                      <Typography variant="body2">
                        {dayjs(finalized.applicationDate).format('MMM D, YYYY')}
                      </Typography>
                    </Box>
                    <Box>
                      <Typography color="text.secondary" variant="caption">
                        Submission Date
                      </Typography>
                      <Typography variant="body2">
                        {dayjs(finalized.submissionDate).format('MMM D, YYYY')}
                      </Typography>
                    </Box>
                    <Box>
                      <Typography color="text.secondary" variant="caption">
                        Expiration Date
                      </Typography>
                      <Typography variant="body2">
                        {dayjs(finalized.expirationDate).format('MMM D, YYYY')}
                      </Typography>
                    </Box>
                    {finalized.allClearDate && (
                      <Box>
                        <Typography color="text.secondary" variant="caption">
                          All Clear Date
                        </Typography>
                        <Typography variant="body2">
                          {dayjs(finalized.allClearDate).format('MMM D, YYYY')}
                        </Typography>
                      </Box>
                    )}
                    {finalized.goLiveDate && (
                      <Box>
                        <Typography color="text.secondary" variant="caption">
                          Go Live Date
                        </Typography>
                        <Typography variant="body2">
                          {dayjs(finalized.goLiveDate).format('MMM D, YYYY')}
                        </Typography>
                      </Box>
                    )}
                  </Box>
                </Box>
                <Box className="flex justify-end mt-3">
                  <Button
                    color="error"
                    size="small"
                    startIcon={<DeleteIcon />}
                    onClick={() => setConfirm('delete-finalized-capce')}
                    disabled={submitting}
                  >
                    Delete
                  </Button>
                </Box>
              </Box>
            )}

            {/* Only pending */}
            {hasOnlyPending && pending && (
              <Box>
                <Box className="grid gap-3">
                  {pending.courseCode && (
                    <Box>
                      <Typography color="text.secondary" variant="caption">
                        Course Code
                      </Typography>
                      <Typography variant="body2">
                        {pending.courseCode}
                      </Typography>
                    </Box>
                  )}
                  {pending.topicArea && (
                    <Box className="grid grid-cols-2 gap-4">
                      <Box>
                        <Typography color="text.secondary" variant="caption">
                          Topic Area
                        </Typography>
                        <Typography variant="body2">
                          {pending.topicArea}
                        </Typography>
                      </Box>
                      {pending.classType && (
                        <Box>
                          <Typography color="text.secondary" variant="caption">
                            Class Type
                          </Typography>
                          <Typography variant="body2">
                            {pending.classType}
                          </Typography>
                        </Box>
                      )}
                    </Box>
                  )}
                  {(pending.applicationDate ||
                    pending.submissionDate ||
                    pending.expirationDate) && (
                    <Box className="grid grid-cols-3 gap-4">
                      {pending.applicationDate && (
                        <Box>
                          <Typography color="text.secondary" variant="caption">
                            Application Date
                          </Typography>
                          <Typography variant="body2">
                            {dayjs(pending.applicationDate).format(
                              'MMM D, YYYY',
                            )}
                          </Typography>
                        </Box>
                      )}
                      {pending.submissionDate && (
                        <Box>
                          <Typography color="text.secondary" variant="caption">
                            Submission Date
                          </Typography>
                          <Typography variant="body2">
                            {dayjs(pending.submissionDate).format(
                              'MMM D, YYYY',
                            )}
                          </Typography>
                        </Box>
                      )}
                      {pending.expirationDate && (
                        <Box>
                          <Typography color="text.secondary" variant="caption">
                            Expiration Date
                          </Typography>
                          <Typography variant="body2">
                            {dayjs(pending.expirationDate).format(
                              'MMM D, YYYY',
                            )}
                          </Typography>
                        </Box>
                      )}
                      {pending.allClearDate && (
                        <Box>
                          <Typography color="text.secondary" variant="caption">
                            All Clear Date
                          </Typography>
                          <Typography variant="body2">
                            {dayjs(pending.allClearDate).format('MMM D, YYYY')}
                          </Typography>
                        </Box>
                      )}
                      {pending.goLiveDate && (
                        <Box>
                          <Typography color="text.secondary" variant="caption">
                            Go Live Date
                          </Typography>
                          <Typography variant="body2">
                            {dayjs(pending.goLiveDate).format('MMM D, YYYY')}
                          </Typography>
                        </Box>
                      )}
                    </Box>
                  )}
                  <AutoTrackedDates
                    submittedDate={pending.submittedDate}
                    approvedDate={pending.approvedDate}
                    rejectedDate={pending.rejectedDate}
                  />
                  {pending.notes && (
                    <Box sx={{ p: 2, bgcolor: 'background.paperDark' }}>
                      <Typography
                        color="text.secondary"
                        variant="caption"
                        sx={{
                          fontWeight: 600,
                        }}
                      >
                        Notes
                      </Typography>
                      <Typography variant="body2">{pending.notes}</Typography>
                    </Box>
                  )}
                </Box>

                <StatusActions
                  status={pending.status}
                  submitting={submitting}
                  onEdit={() => setEditFormOpen(true)}
                  onDelete={() => setConfirm('delete-pending-capce')}
                  onFinalize={handleFinalize}
                />
              </Box>
            )}
          </Box>
        </Collapse>
      </Card>
      <CreateForm
        eventId={eventId}
        courseId={courseId}
        finalized={finalized}
        open={formOpen}
        setOpen={setFormOpen}
        onSuccess={() => {}}
      />
      {pending && (
        <EditForm
          eventId={eventId}
          courseId={courseId}
          pending={pending}
          finalized={finalized}
          open={editFormOpen}
          setOpen={setEditFormOpen}
          onSuccess={() => {}}
        />
      )}
      {pending && (
        <ConfirmAction
          open={confirm}
          setOpen={setConfirm}
          openText="delete-pending-capce"
          title="Delete Pending CAPCE?"
          text="Are you sure you want to delete this pending CAPCE accreditation? This action cannot be undone."
          confirmText="Delete"
          executeAction={async () => {
            if (courseId) {
              dispatch(deletePendingCAPCECourse({ id: pending.id, courseId }))
            } else if (eventId) {
              dispatch(deletePendingCAPCE({ id: pending.id, eventId }))
            }
          }}
        />
      )}
      {finalized && (
        <ConfirmAction
          open={confirm}
          setOpen={setConfirm}
          openText="delete-finalized-capce"
          title="Delete CAPCE Accreditation?"
          text="Are you sure you want to delete this CAPCE accreditation? This action cannot be undone."
          confirmText="Delete"
          executeAction={async () => {
            if (courseId) {
              dispatch(deleteCAPCECourse({ id: finalized.id, courseId }))
            } else if (eventId) {
              dispatch(deleteCAPCE({ id: finalized.id, eventId }))
            }
          }}
        />
      )}
      <OperationSnackbars
        successMessage={operationSuccess}
        errorMessage={operationError}
        onClear={() => dispatch(clearCapceOperationStatus())}
      />
    </Box>
  )
}
